import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatDate, titleCase } from '../../lib/format'
import type { MemberProfile, Organization } from '../../types'
import { Alert, Button, PageLoader } from '../../components/ui'
import { Logo } from '../../components/Logo'
import { IdIcon } from '../../components/icons'
import './badge.css'

interface MemberResponse { ok: boolean; profile: MemberProfile }
interface OrgResponse { organization: Organization }

/** A short, deterministic badge number derived from the uid — this
 * platform has no auto-numbering scheme for staff IDs (confirmed: none
 * exists anywhere in the codebase), so re-printing the same person's
 * badge always shows the same number rather than a new random one. */
function badgeNumber(uid: string): string {
  return `ID-${uid.replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase()}`
}

export default function StaffBadge() {
  const { uid } = useParams<{ uid: string }>()
  const { user } = useAuth()
  const orgId = user?.org_id

  const memberQuery = useQuery({
    queryKey: ['leader', 'team', uid],
    queryFn: () => api.get<MemberResponse>(`/leader/team/${uid}`),
    enabled: Boolean(uid),
  })
  const orgQuery = useQuery({
    queryKey: ['leader', 'org', orgId],
    queryFn: () => api.get<OrgResponse>(`/organizations/${orgId}`),
    enabled: Boolean(orgId),
  })

  if (!uid) return <Navigate to="/team" replace />

  if (memberQuery.isError) {
    return (
      <Alert tone="danger">
        {(memberQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/team">Back to team</Link></div>
      </Alert>
    )
  }
  if (memberQuery.isLoading || !memberQuery.data) return <PageLoader label="Loading badge…" />

  const profile = memberQuery.data.profile
  const orgName = orgQuery.data?.organization.name || 'SSM Zambia'

  return (
    <div className="badge-page">
      <div className="page-head no-print">
        <div>
          <h1>ID badge</h1>
          <p className="page-head__sub">Print this for {profile.name || profile.email}.</p>
        </div>
        <div className="row">
          <Link to={`/team/${uid}`}><Button variant="secondary">Back</Button></Link>
          <Button icon={<IdIcon size={15} />} onClick={() => window.print()}>Print</Button>
        </div>
      </div>

      {!profile.photo_url && (
        <div className="no-print" style={{ width: 340 }}>
          <Alert tone="warning">
            No photo on file yet — <Link to={`/team/${uid}`}>upload one</Link> before printing for a
            complete badge.
          </Alert>
        </div>
      )}

      <div className="id-card">
        <div className="id-card__header">
          <Logo tone="onDark" tagline="Staff ID" />
          <div className="id-card__org">{orgName}</div>
        </div>
        <div className="id-card__body">
          <div className="id-card__title">Staff ID</div>
          <span className="id-card__pill">Site access</span>

          <div className="id-card__photo-wrap">
            {profile.photo_url
              ? <img src={profile.photo_url} alt="" />
              : <span className="id-card__photo-empty">No photo on file</span>}
          </div>

          <div className="id-card__info">
            <div className="id-card__row">
              <span className="id-card__label">Name</span>
              <span className="id-card__value">{profile.name || '—'}</span>
            </div>
            <div className="id-card__row">
              <span className="id-card__label">Title</span>
              <span className="id-card__value">{titleCase(profile.role)}</span>
            </div>
            <div className="id-card__row">
              <span className="id-card__label">ID no.</span>
              <span className="id-card__value">{badgeNumber(uid)}</span>
            </div>
            <div className="id-card__row">
              <span className="id-card__label">Issued</span>
              <span className="id-card__value">{formatDate(new Date().toISOString())}</span>
            </div>
          </div>
        </div>
        <div className="id-card__footer">
          <span>SSM Zambia</span>
          <span>Verify at ssmzambia.com</span>
        </div>
      </div>
    </div>
  )
}
